/* ============================================================================
   SpecificationListTab.jsx
   ----------------------------------------------------------------------------
   OVERVIEW / MAP — cartographie décisionnelle des spécifications du projet

   RÔLE
   - montrer où se situent les zones de tension
   - orienter l’utilisateur vers les sujets à analyser
   - rester sobre, lisible, métier
============================================================================ */

import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";
import InfoPanel from "../../reusable/Forms/InfoPanel";

/* ============================================================================
   TOKENS
============================================================================ */

const T = {
  bgSurface: theme?.colors?.background?.surface || "#ffffff",
  bgHover: theme?.colors?.background?.hover || "#f7f9fc",
  bgPanel: theme?.colors?.background?.panel || "#f1f3f6",

  border: theme?.colors?.borders?.default || "#d6dee4",
  borderPanel: theme?.colors?.borders?.panel || "#cfd8dd",
  borderStrong: theme?.colors?.borders?.darkblue || "#b2c1c7",

  text: theme?.colors?.text?.primary || "#164D63",
  textBody: theme?.colors?.text?.body || "#3F5D6E",
  textSmall: theme?.colors?.text?.small || "#5C7A89",
  textVerySmall: theme?.colors?.text?.verysmall || "#7D97A1",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",
  green: theme?.colors?.text?.green || "#1A8C74",
  orange: theme?.colors?.text?.secondary || "#B8621B",

  bluePastel: theme?.Generic?.blue?.pastel?.bg || "#E8F2F6",
  bluePastelText: theme?.Generic?.blue?.pastel?.text || "#1E5066",

  greenPastel: theme?.Generic?.green?.pastel?.bg || "#E7F3ED",
  greenPastelText: theme?.Generic?.green?.pastel?.text || "#166A58",

  yellowPastel: theme?.Generic?.yellow?.pastel?.bg || "#F9F3E3",
  yellowPastelText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",

  orangePastel: theme?.Generic?.orange?.pastel?.bg || "#F8ECE5",
  orangePastelText: theme?.Generic?.orange?.pastel?.text || "#9A4315",

  greyPastel: theme?.Generic?.grey?.pastel?.bg || "#EDF1F4",
  greyPastelText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
};

/* ============================================================================
   DONNÉES DEMO
   ----------------------------------------------------------------------------
   Volontairement enrichies pour tester la lecture, le scroll et le filtrage.
============================================================================ */

const MAP_TREE = [
  {
    id: "1",
    label: "Réservoir",
    discipline: "Structure",
    criticite: "Critique",
    maturityStatus: "À consolider",
    technicalStatus: "Partielle",
    projectImpact: "Critique",
    synthesis:
      "Couverture partielle sur la protection de l’ouvrage et interfaces encore sensibles sur drainage et étanchéité.",
    objets: ["Étanchéité", "Durabilité béton"],
    interfaces: ["Hydraulique", "Génie civil"],
    specsGenerees: 28,
    sousTension: 11,
    maitrisees: 17,
    children: [
      {
        id: "1-1",
        label: "Fondations réservoir",
        discipline: "Génie civil",
        criticite: "Critique",
        maturityStatus: "En cours",
        technicalStatus: "Instable",
        projectImpact: "Bloquant",
        synthesis:
          "Le drainage périphérique reste incomplet et plusieurs hypothèses de traitement ne sont pas stabilisées.",
        objets: ["Cuvelage", "Drainage", "Béton"],
        interfaces: ["Hydraulique", "Génie civil"],
      },
      {
        id: "1-2",
        label: "Voiles",
        discipline: "Structure",
        criticite: "Haute",
        maturityStatus: "À consolider",
        technicalStatus: "Partielle",
        projectImpact: "Critique",
        synthesis:
          "Les pénétrations et la logique d’étanchéité doivent être harmonisées avant consolidation.",
        objets: ["Protection surface", "Fissuration"],
        interfaces: ["Architecture", "Hydraulique"],
      },
    ],
  },
  {
    id: "2",
    label: "Station de pompage",
    discipline: "Hydraulique",
    criticite: "Critique",
    maturityStatus: "En cours",
    technicalStatus: "Instable",
    projectImpact: "Bloquant",
    synthesis:
      "Les fonctions critiques sont identifiées mais plusieurs interfaces de commande et de secours restent non stabilisées.",
    objets: ["Fonctionnement", "Commande"],
    interfaces: ["Électricité", "Automatisme"],
    specsGenerees: 34,
    sousTension: 15,
    maitrisees: 19,
    children: [
      {
        id: "2-1",
        label: "Groupe de pompage",
        discipline: "Hydraulique",
        criticite: "Critique",
        maturityStatus: "En cours",
        technicalStatus: "Instable",
        projectImpact: "Bloquant",
        synthesis:
          "Continuité de service fragilisée par une définition encore partielle des logiques de secours et de commande.",
        objets: ["Pompage", "Maintenance"],
        interfaces: ["Électricité", "Automatisme"],
      },
      {
        id: "2-2",
        label: "Armoire de commande",
        discipline: "Électricité",
        criticite: "Haute",
        maturityStatus: "À consolider",
        technicalStatus: "Partielle",
        projectImpact: "Critique",
        synthesis:
          "Les échanges de signaux et les responsabilités d’interface doivent encore être arbitrés.",
        objets: ["Commande", "Retour d’état"],
        interfaces: ["Hydraulique", "Automatisme"],
      },
    ],
  },
  {
    id: "3",
    label: "Bâtiment technique",
    discipline: "Architecture",
    criticite: "Moyenne",
    maturityStatus: "En cours",
    technicalStatus: "Partielle",
    projectImpact: "Sous contrôle",
    synthesis:
      "Le périmètre est globalement lisible mais certaines réservations et contraintes d’exploitation restent ouvertes.",
    objets: ["Accès", "Réservations"],
    interfaces: ["Génie civil", "Exploitation"],
    specsGenerees: 18,
    sousTension: 6,
    maitrisees: 12,
    children: [
      {
        id: "3-1",
        label: "Local électrique",
        discipline: "Électricité",
        criticite: "Haute",
        maturityStatus: "En cours",
        technicalStatus: "Partielle",
        projectImpact: "Critique",
        synthesis:
          "Ventilation et niveaux de protection encore à confirmer avant consolidation.",
        objets: ["Ventilation", "Protection"],
        interfaces: ["Architecture", "Électricité"],
      },
      {
        id: "3-2",
        label: "Traversées techniques",
        discipline: "Génie civil",
        criticite: "Critique",
        maturityStatus: "En cours",
        technicalStatus: "Partielle",
        projectImpact: "Critique",
        synthesis:
          "Plusieurs réservations restent ouvertes entre structure, équipements et exploitation.",
        objets: ["Traversées", "Interfaces GC"],
        interfaces: ["Hydraulique", "Équipements"],
      },
    ],
  },
];

/* ============================================================================
   HELPERS
============================================================================ */

const flattenTree = (nodes, level = 0, parentId = null) => {
  let rows = [];

  nodes.forEach((node) => {
    rows.push({ ...node, level, parentId });
    if (node.children?.length) {
      rows = rows.concat(flattenTree(node.children, level + 1, node.id));
    }
  });

  return rows;
};

const getDisciplineTone = (value) => {
  const map = {
    Structure: {
      background: "#edf4ff",
      border: "1px solid #cfe0ff",
      color: "#2f5fa8",
    },
    "Génie civil": {
      background: "#ebf8f3",
      border: "1px solid #c8eadc",
      color: "#1f7a63",
    },
    Hydraulique: {
      background: "#ecfbfc",
      border: "1px solid #c7eef1",
      color: "#117a8b",
    },
    Électricité: {
      background: "#f4efff",
      border: "1px solid #ddd0ff",
      color: "#6a43c1",
    },
    Automatisme: {
      background: "#f4efff",
      border: "1px solid #ddd0ff",
      color: "#6a43c1",
    },
    Architecture: {
      background: "#fff9e8",
      border: "1px solid #f4e4a6",
      color: "#9a7808",
    },
    Exploitation: {
      background: "#edf1f4",
      border: "1px solid #d7e0e5",
      color: "#4b6270",
    },
  };

  return (
    map[value] || {
      background: "#f8fafc",
      border: `1px solid ${T.border}`,
      color: T.textSmall,
    }
  );
};

const getImpactBarColor = (value) => {
  if (value === "Bloquant") return T.caneva;
  if (value === "Critique") return T.orange;
  if (value === "Sous contrôle") return T.corelia;
  return T.borderStrong;
};

const getMaturityStyle = (value) => {
  const map = {
    "Non traitée": { bg: T.greyPastel, text: T.greyPastelText },
    "En cours": { bg: T.bluePastel, text: T.bluePastelText },
    "À consolider": { bg: T.yellowPastel, text: T.yellowPastelText },
    Validée: { bg: T.greenPastel, text: T.greenPastelText },
  };

  return map[value] || { bg: T.greyPastel, text: T.greyPastelText };
};

const getTechnicalStyle = (value) => {
  const map = {
    "Non définie": { bg: T.greyPastel, text: T.greyPastelText },
    Instable: { bg: T.orangePastel, text: T.orangePastelText },
    Partielle: { bg: T.yellowPastel, text: T.yellowPastelText },
    "Maîtrisée": { bg: T.greenPastel, text: T.greenPastelText },
  };

  return map[value] || { bg: T.greyPastel, text: T.greyPastelText };
};

const getImpactTextColor = (value) => {
  const map = {
    Bloquant: T.caneva,
    Critique: T.orange,
    "Sous contrôle": T.corelia,
    Neutre: T.textSmall,
  };
  return map[value] || T.textSmall;
};

const isUnderTension = (row) =>
  row.projectImpact === "Bloquant" ||
  row.projectImpact === "Critique" ||
  row.technicalStatus === "Instable" ||
  row.maturityStatus === "À consolider" ||
  row.maturityStatus === "Non traitée";

const isInterfaceRow = (row) => (row.interfaces || []).length > 0;

const isNonCovered = (row) =>
  row.maturityStatus === "Non traitée" || row.technicalStatus === "Non définie";

/* ============================================================================
   UI ATOMS
============================================================================ */

const FilterPill = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "fit-content",
      height: 28,
      padding: "0 12px",
      borderRadius: theme?.borders?.radius?.full || 999,
      border: `1px solid ${active ? T.corelia : T.border}`,
      background: active ? T.bluePastel : "#ffffff",
      color: active ? T.corelia : T.textBody,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.74rem",
      fontWeight: theme?.fontWeights?.medium || 500,
      lineHeight: 1,
      whiteSpace: "nowrap",
      cursor: "pointer",
    }}
  >
    {label}
  </button>
);

const MicroTag = ({ label, bg, text }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 8px",
      borderRadius: theme?.borders?.radius?.full || 9999,
      background: bg,
      color: text,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.69rem",
      lineHeight: 1,
      fontWeight: 500,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </div>
);

const DisciplineTag = ({ value }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 9px",
      borderRadius: theme?.borders?.radius?.full || 999,
      whiteSpace: "nowrap",
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.69rem",
      fontWeight: 500,
      lineHeight: 1,
      ...getDisciplineTone(value),
    }}
  >
    {value}
  </span>
);

/* ============================================================================
   COMPONENT
============================================================================ */

const SpecificationListTab = ({ onSelectItem }) => {
  const [expanded, setExpanded] = useState({
    "1": false,
    "2": false,
    "3": false,
  });

  const [selectedId, setSelectedId] = useState("2-1");
  const [viewMode, setViewMode] = useState("ALL");
  const [hoveredId, setHoveredId] = useState(null);

  const flatRows = useMemo(() => flattenTree(MAP_TREE), []);

  const summary = useMemo(() => {
    return flatRows.reduce(
      (acc, row) => {
        acc.total += row.specsGenerees || 0;
        acc.sousTension += row.sousTension || 0;
        acc.maitrisees += row.maitrisees || 0;
        return acc;
      },
      { total: 0, sousTension: 0, maitrisees: 0 }
    );
  }, [flatRows]);

  const visibleRows = useMemo(() => {
    const isVisibleByTree = (row) => {
      let currentParentId = row.parentId;

      while (currentParentId) {
        if (!expanded[currentParentId]) return false;
        currentParentId =
          flatRows.find((r) => r.id === currentParentId)?.parentId || null;
      }

      return true;
    };

    const isVisibleByFilter = (row) => {
      if (viewMode === "ALL") return true;
      if (viewMode === "TENSION") return isUnderTension(row);
      if (viewMode === "INTERFACES") return isInterfaceRow(row);
      if (viewMode === "NON_COUVERT") return isNonCovered(row);
      return true;
    };

    return flatRows.filter(
      (row) => isVisibleByTree(row) && isVisibleByFilter(row)
    );
  }, [expanded, flatRows, viewMode]);

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelect = (row) => {
    setSelectedId(row.id);
    onSelectItem?.(row);
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        boxSizing: "border-box",
        paddingTop: 0,
        paddingRight: theme?.spacing?.sm || 6,
        paddingBottom: theme?.spacing?.sm || 6,
        paddingLeft: theme?.spacing?.sm || 6,
      }}
    >
      {/* ============================================================
         BANDE HAUTE
      ============================================================ */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {/* <InfoPanel
          variant="blue"
          title="Dossier sous tension"
          message="Des interfaces critiques et des zones de couverture incomplète nécessitent une lecture analytique ciblée."
        /> */}

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
              flex: 1,
            }}
          >
            <FilterPill
              label="Tous"
              active={viewMode === "ALL"}
              onClick={() => setViewMode("ALL")}
            />
            <FilterPill
              label="Sous tension"
              active={viewMode === "TENSION"}
              onClick={() => setViewMode("TENSION")}
            />
            <FilterPill
              label="Interfaces"
              active={viewMode === "INTERFACES"}
              onClick={() => setViewMode("INTERFACES")}
            />
            <FilterPill
              label="Non couvert"
              active={viewMode === "NON_COUVERT"}
              onClick={() => setViewMode("NON_COUVERT")}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            <InfoPanel
              title="Spécifications"
              message={String(summary.total)}
              variant="white"
              size="sm"
              stripPosition="left"
              border={true}
              style={{
                width: "auto",
                minWidth: 104,
                maxWidth: 132,
                margin: 0,
                overflow: "hidden",
                flex: "0 0 auto",
              }}
            />
            <InfoPanel
              title="Sous tension"
              message={String(summary.sousTension)}
              variant="yellow"
              size="sm"
              stripPosition="left"
              border={true}
              style={{
                width: "auto",
                minWidth: 104,
                maxWidth: 132,
                margin: 0,
                overflow: "hidden",
                flex: "0 0 auto",
              }}
            />
            <InfoPanel
              title="Maîtrisées"
              message={String(summary.maitrisees)}
              variant="green"
              size="sm"
              stripPosition="left"
              border={true}
              style={{
                width: "auto",
                minWidth: 104,
                maxWidth: 132,
                margin: 0,
                overflow: "hidden",
                flex: "0 0 auto",
              }}
            />
          </div>
        </div>
      </div>

      {/* ============================================================
         LISTE ANALYTIQUE
      ============================================================ */}
      <div
        style={{
          width: "100%",
          maxHeight: 620,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          paddingRight: 4,
          boxSizing: "border-box",
        }}
      >
        {visibleRows.map((row) => {
          const selected = row.id === selectedId;
          const hovered = row.id === hoveredId;

          const maturityStyle = getMaturityStyle(row.maturityStatus);
          const technicalStyle = getTechnicalStyle(row.technicalStatus);

          const hasChildren = row.children?.length > 0;

          return (
            <div
              key={row.id}
              onClick={() => handleSelect(row)}
              onMouseEnter={() => setHoveredId(row.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                display: "grid",
                gridTemplateColumns: "4px minmax(170px, 26%) minmax(0, 1fr) auto minmax(110px, 14%)",
                gap: 12,
                alignItems: "stretch",
                border: `1px solid ${selected ? T.borderStrong : T.border}`,
                background: selected
                  ? "#f6fafc"
                  : hovered
                  ? T.bgHover
                  : "#ffffff",
                padding: "10px 12px 10px 0",
                cursor: "pointer",
              }}
            >
              {/* Barre d'état */}
              <div
                style={{
                  background: getImpactBarColor(row.projectImpact),
                }}
              />

              {/* Élément */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  paddingLeft: `${row.level * 18}px`,
                  minWidth: 0,
                }}
              >
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(row.id);
                    }}
                    style={{
                      width: 14,
                      height: 14,
                      border: `1px solid ${T.border}`,
                      borderRadius: 2,
                      background: "#ffffff",
                      color: T.textSmall,
                      fontSize: 10,
                      lineHeight: "12px",
                      padding: 0,
                      marginTop: 2,
                      cursor: "pointer",
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {expanded[row.id] ? "−" : "+"}
                  </button>
                ) : (
                  <div
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: 999,
                      background: T.corelia,
                      marginTop: 6,
                      marginLeft: 4,
                      flexShrink: 0,
                    }}
                  />
                )}

                <div
                  style={{
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      fontFamily: theme?.fonts?.body || "inherit",
                      fontSize: "0.83rem",
                      fontWeight: theme?.fontWeights?.medium || 500,
                      lineHeight: 1.2,
                      color: T.text,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {row.label}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    <DisciplineTag value={row.discipline} />
                  </div>
                </div>
              </div>

              {/* Lecture synthétique */}
              <div
                style={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: theme?.fonts?.body || "inherit",
                    fontSize: "0.77rem",
                    lineHeight: 1.35,
                    color: T.textBody,
                  }}
                >
                  {row.synthesis}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    alignItems: "center",
                  }}
                >
                  {(row.objets || []).map((o) => (
                    <div
                      key={o}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "4px 8px",
                        border: `1px solid ${T.border}`,
                        borderRadius: 5,
                        background: "#ffffff",
                        color: T.textBody,
                        fontFamily: theme?.fonts?.body || "inherit",
                        fontSize: "0.68rem",
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {o}
                    </div>
                  ))}
                </div>
              </div>

              {/* Statuts */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  alignContent: "center",
                  minWidth: 0,
                }}
              >
                <MicroTag
                  label={row.maturityStatus}
                  bg={maturityStyle.bg}
                  text={maturityStyle.text}
                />
                <MicroTag
                  label={row.technicalStatus}
                  bg={technicalStyle.bg}
                  text={technicalStyle.text}
                />
                <div
                  style={{
                    fontFamily: theme?.fonts?.body || "inherit",
                    fontSize: "0.69rem",
                    lineHeight: 1.1,
                    color: getImpactTextColor(row.projectImpact),
                    fontWeight: 500,
                    paddingTop: 4,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.projectImpact}
                </div>
              </div>

              {/* Interfaces */}
              <div
                style={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  justifyContent: "center",
                }}
              >
                {(row.interfaces || []).slice(0, 2).map((i) => (
                  <div
                    key={i}
                    style={{
                      fontFamily: theme?.fonts?.body || "inherit",
                      fontSize: "0.72rem",
                      lineHeight: 1.2,
                      color: T.textBody,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {i}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SpecificationListTab;